'use client';

import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './Modal.module.scss';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
}

function useScrollLock(locked: boolean) {
    useEffect(() => {
        if (!locked) return;

        const html = document.documentElement;
        const body = document.body;

        // зберігаємо попередні inline-стилі
        const prevHtmlOverflow = html.style.overflow;
        const prevBodyOverflow = body.style.overflow;
        const prevBodyPosition = body.style.position;
        const prevBodyTop = body.style.top;
        const prevBodyLeft = body.style.left;
        const prevBodyRight = body.style.right;
        const scrollY = window.scrollY;

        // Лок: Safari-friendly
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';

        return () => {
            // Відновлення
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
            body.style.position = prevBodyPosition;
            body.style.top = prevBodyTop;
            body.style.left = prevBodyLeft;
            body.style.right = prevBodyRight;
            body.style.width = '';

            // повертаємо прокрутку туди, де була
            const y = scrollY || (prevBodyTop ? -parseInt(prevBodyTop, 10) : 0);
            window.scrollTo(0, y);
        };
    }, [locked]);
}

export const Modal = ({ open, onClose, children }: ModalProps) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => setMounted(true), []);

    // 🔒 блокуємо скрол коли модалка відкрита
    useScrollLock(open);

    if (!mounted || !open) return null;

    const root = document.getElementById('modal-root');
    if (!root) return null;

    return createPortal(
        <div className={s.backdrop} onClick={onClose} role="dialog" aria-modal="true">
            <div className={s.modal} onClick={(e) => e.stopPropagation()}>
                <button className={s.close} onClick={onClose} aria-label="Закрити">
                    ×
                </button>
                {children}
            </div>
        </div>,
        root
    );
};
